import React from "react";
import {
  Activity,
  Eye,
  MousePointerClick,
  Package,
  Search,
  Users,
  Radio,
  TrendingUp,
} from "lucide-react";

interface AnalyticsSummaryData {
  totalEvents?: number;
  bannerViews?: number;
  bannerClicks?: number;
  productViews?: number;
  searches?: number;
  uniqueUsers?: number;
  uniqueSessions?: number;
  bannerClickThroughRate?: number;
}

interface AnalyticsSummaryProps {
  summary?: AnalyticsSummaryData;
}

const AnalyticsSummary: React.FC<AnalyticsSummaryProps> = ({ summary }) => {
  const cards = [
    {
      title: "Total Events",
      value: summary?.totalEvents ?? 0,
      icon: Activity,
      iconClass: "bg-logoHeading/10 text-logoHeading",
    },
    {
      title: "Banner Views",
      value: summary?.bannerViews ?? 0,
      icon: Eye,
      iconClass: "bg-blue-50 text-blue-600",
    },
    {
      title: "Banner Clicks",
      value: summary?.bannerClicks ?? 0,
      icon: MousePointerClick,
      iconClass: "bg-emerald-50 text-emerald-600",
    },
    {
      title: "Product Views",
      value: summary?.productViews ?? 0,
      icon: Package,
      iconClass: "bg-violet-50 text-violet-600",
    },
    {
      title: "Searches",
      value: summary?.searches ?? 0,
      icon: Search,
      iconClass: "bg-amber-50 text-amber-600",
    },
    {
      title: "Unique Users",
      value: summary?.uniqueUsers ?? 0,
      icon: Users,
      iconClass: "bg-pink-50 text-pink-600",
    },
    {
      title: "Unique Sessions",
      value: summary?.uniqueSessions ?? 0,
      icon: Radio,
      iconClass: "bg-cyan-50 text-cyan-600",
    },
    {
      title: "Banner CTR",
      value: `${(summary?.bannerClickThroughRate ?? 0).toFixed(2)}%`,
      icon: TrendingUp,
      iconClass: "bg-indigo-50 text-indigo-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <div
            key={card.title}
            className="
              bg-white
              rounded-2xl
              border border-gray-100
              shadow-sm
              px-5 py-4
              transition-all
              duration-200
              hover:shadow-md
            "
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium text-gray-400">
                  {card.title}
                </p>

                <p className="mt-2 text-2xl font-bold text-gray-800 tracking-tight">
                  {typeof card.value === "number"
                    ? card.value.toLocaleString()
                    : card.value}
                </p>
              </div>

              <div
                className={`
                  w-11 h-11
                  rounded-xl
                  flex items-center justify-center
                  ${card.iconClass}
                `}
              >
                <Icon size={19} strokeWidth={2} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AnalyticsSummary;
